import React, { useState, useRef } from 'react';
import FullCalendar from '@fullcalendar/react'; 
import dayGridPlugin from '@fullcalendar/daygrid'; //  for Month view
import interactionPlugin from '@fullcalendar/interaction'; // For interaction (date selection)

const CalendarWidget = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('week'); // 'week', 'month', 'year'
  const [selectedDayTasks, setSelectedDayTasks] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null); // Selected day for weekly tasks
  const calendarRef = useRef(null);

  
  const weeklyTasks = {
    Sunday: ['Task 1', 'Task 2'],
    Monday: ['Task A', 'Task B'],
    Tuesday: ['Project 1', 'Meeting with client'],
    Wednesday: ['Task X'],
    Thursday: ['Project Finalization'],
    Friday: ['Demo Preparation'],
    Saturday: ['Relax'],
  };


  const getWeekRange = () => {
    const start = new Date(currentDate);
    start.setDate(start.getDate() - start.getDay());
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
  };


  const handlePrevMonth = () => {
    if (viewMode === 'year') {
      // Handle year view navigation
      const newDate = new Date(currentDate.getFullYear() - 1, currentDate.getMonth());
      setCurrentDate(newDate);
    } else if (viewMode === 'month') {
      const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1);
      setCurrentDate(newDate);
      if (calendarRef.current) {
        calendarRef.current.getApi().prev();
      }
    } else {
      // Week view
      setCurrentDate(new Date(currentDate.setDate(currentDate.getDate() - 7)));
    }
  };


  const handleNextMonth = () => {
    if (viewMode === 'year') {
      // Handle year view navigation
      const newDate = new Date(currentDate.getFullYear() + 1, currentDate.getMonth());
      setCurrentDate(newDate);
    } else if (viewMode === 'month') {
      const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1);
      setCurrentDate(newDate);
      if (calendarRef.current) {
        calendarRef.current.getApi().next();
      }
    } else {
      // Week view
      setCurrentDate(new Date(currentDate.setDate(currentDate.getDate() + 7)));
    }
  };


  //WEEK
  const renderWeekView = () => {
    const hours = Array.from({ length: 24 }, (_, i) => `${i}:00`);
    const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  
    return (
      <div style={styles.weekView.container}>
        <div style={styles.weekView.scrollContainer}>
          <div style={styles.weekView.gridWrapper}>
            {/* Header Row */}
            <div style={styles.weekView.timeColumn}></div>
            {daysOfWeek.map((day, index) => (
              <div key={index} style={styles.weekView.dayHeader}>{day}</div>
            ))}
  
            {/* Time Column + Day Columns */}
            {hours.map((hour, hourIndex) => (
              <React.Fragment key={hourIndex}>
                {/* Time Column */}
                <div style={styles.weekView.timeColumn}>{hour}</div>
  
                {/* Day Columns */}
                {daysOfWeek.map((_, dayIndex) => (
                  <div key={dayIndex} style={styles.weekView.dayColumn}></div>
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    );
  };


  //MONTH VIEW
  const renderMonthView = () => {
    return (
      <div style={styles.monthView.container}>
        {/* Calendar Section */}
        <div style={styles.monthView.calendarWrapper}>
          <FullCalendar
            plugins={[dayGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            headerToolbar={false} 
            events={[
              { title: 'Task 1', start: '2024-11-25' },
              { title: 'Meeting', start: '2024-11-27' },
            ]}
            height="auto"
            contentHeight="auto"
            dayHeaderFormat={{ weekday: 'long' }}
            firstDay={1} // Start the week on Monday
          />
        </div>
      </div>
    );
  };
  

  //YEAR VIEW
  const renderYearView = () => {
    const months = Array.from({ length: 12 }, (_, i) => new Date(currentDate.getFullYear(), i));
  
    return (
      <div style={styles.yearView.container}>
        {months.map((month, index) => (
          <div
            key={index}
            style={styles.yearView.monthBox}
            onClick={() => {
              const newDate = new Date(currentDate.getFullYear(), index);
              setCurrentDate(newDate);
              setViewMode('month');
              if (calendarRef.current) {
                calendarRef.current.getApi().gotoDate(newDate);
              }
            }}
          >
            <strong>{month.toLocaleString('default', { month: 'long' })}</strong>
          </div>
        ))}
      </div>
    );
  };
  

  return (
    <div style={styles.container}>
      {/* Calendar */}
      <div style={styles.calendarContainer}>
        <div style={styles.header}>
          <button
            style={styles.arrowButton}
            onClick={handlePrevMonth}
          >
            &lt;
          </button>
          <div style={styles.title}>
            {viewMode === 'week'
              ? `Week of ${getWeekRange()}`
              : viewMode === 'month'
              ? `Month View: ${currentDate.toLocaleString('default', { month: 'long' })} ${currentDate.getFullYear()}`
              : `Year View: ${currentDate.getFullYear()}`}
          </div>
          <button
            style={styles.arrowButton}
            onClick={handleNextMonth}
          >
            &gt;
          </button>
        </div>

        {/* Render Views */}
        {viewMode === 'week' && renderWeekView()}
        {viewMode === 'month' && renderMonthView()}
        {viewMode === 'year' && renderYearView()}

        {/* View Switcher */}
        <div style={styles.viewSwitcher}>
          <button
            style={{ ...styles.switchButton, backgroundColor: viewMode === 'week' ? '#007bff' : '#ccc' }}
            onClick={() => setViewMode('week')}
          >
            Week
          </button>
          <button
            style={{ ...styles.switchButton, backgroundColor: viewMode === 'month' ? '#007bff' : '#ccc' }}
            onClick={() => setViewMode('month')}
          >
            Month
          </button>
          <button
            style={{ ...styles.switchButton, backgroundColor: viewMode === 'year' ? '#007bff' : '#ccc' }}
            onClick={() => setViewMode('year')}
          >
            Year
          </button>
        </div>
      </div>

      {/* Upcoming Events */}
      <div style={styles.upcomingEvents}>
        <h4>Upcoming Events</h4>
        <ul>
          <li>
            <strong>Event 1:</strong> Meeting on {new Date().toDateString()}
          </li>
          <li>
            <strong>Event 2:</strong> Deadline on {new Date().toDateString()}
          </li>
        </ul>
      </div>
    </div>
  );
};

const styles = {
  // Main container layout
  container: {
    display: 'flex',
    padding: '10px',
    height: '500px',
    width: '100%',
    gap: '20px',
  },
  calendarContainer: {
    flex: '1 1 auto',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    border: '1px solid #ddd',
    borderRadius: '5px',
    overflow: 'hidden',
  },
  upcomingEvents: {
    width: '200px',
    flex: '0 0 200px',
    backgroundColor: '#f8f9fa',
    border: '1px solid #ddd',
    borderRadius: '5px',
    padding: '10px',
    height: '100%',
    overflow: 'auto',
    marginLeft: '20px',
  },

  // General Styles
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px',
  },
  arrowButton: {
    padding: '5px 10px',
    fontSize: '1rem',
    cursor: 'pointer',
    backgroundColor: '#007bff',
    color: '#fff',
    border: 'none',
    borderRadius: '5px',
  },
  title: {
    fontSize: '1.2rem',
    fontWeight: 'bold',
  },
  viewSwitcher: {
    display: 'flex',
    justifyContent: 'center',
    gap: '10px',
    marginTop: '10px',
  },
  switchButton: {
    padding: '5px 10px',
    border: '1px solid #ccc',
    borderRadius: '5px',
    cursor: 'pointer',
  },
  // WEEK VIEW
  weekView: {
    container: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      height: '90vh',
      overflow: 'hidden',
    },
    scrollContainer: {
      display: 'flex',
      flexDirection: 'column',
      overflowX: 'auto',
      flex: 1,
    },
    gridWrapper: {
      display: 'grid',
      gridTemplateColumns: '60px repeat(7, 1fr)', 
      width: '100%',
      borderCollapse: 'collapse',
    },
    headerRow: {
      display: 'contents',
    },
    timeColumn: {
      width: '60px', 
      backgroundColor: '#f8f9fa',
      borderRight: '1px solid #ddd',
      textAlign: 'center',
      fontWeight: 'bold',
    },
    dayHeader: {
      borderRight: '1px solid #ddd',
      backgroundColor: '#f8f9fa',
      textAlign: 'center',
      fontWeight: 'bold',
      padding: '10px 0',
    },
    body: {
      display: 'contents',
    },
    dayColumn: {
      borderRight: '1px solid #ddd',
      borderBottom: '1px solid #ddd',
    },
    timeSlot: {
      height: '50px',
      textAlign: 'center',
      lineHeight: '50px',
      borderBottom: '1px solid #ddd',
    },
  },
  
// Month View Styles
monthView: {
  container: {
    display: 'flex',
    flexDirection: 'column', // Stack elements vertically
    height: '90vh',          // Use 90% of the viewport height
    width: '100%',
    gap: '10px',
    overflow: 'hidden',      // Prevent unnecessary scrollbars
  },

  calendarWrapper: {
    flex: '1 1 auto',       // Allow the calendar to take up all available space
    width: '100%',
    overflowY: 'auto',      // Enable vertical scrolling
    backgroundColor: '#fff',
    border: '1px solid #ddd',
    borderRadius: '5px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)', // Optional shadow
  },

  viewSwitcher: {
    display: 'flex',
    justifyContent: 'center',
    gap: '10px',
    marginTop: '10px',
  },

  viewButton: {
    padding: '8px 15px',
    fontSize: '1rem',
    color: '#fff',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
    backgroundColor: '#007bff', // Default blue for active buttons
  },

  inactiveViewButton: {
    backgroundColor: '#ccc', // Grey for inactive buttons
  },
},

  // Year View Styles
  yearView: {
    container: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: '10px',
    },
    monthBox: {
      border: '1px solid #ccc',
      padding: '10px',
      textAlign: 'center',
      borderRadius: '5px',
      backgroundColor: '#f8f9fa',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      height: '100%',
      cursor: 'pointer',
    },
  },

  // Week View Layout
  weekContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
    overflow: 'hidden',
  },
  weekContent: {
    display: 'flex',
    overflowX: 'auto', // Enable horizontal scroll
    width: '100%',
  },
  weekdaySection: {
    display: 'flex',
    minWidth: '100%', // Ensures Mon-Fri take full width
    flex: 'none', // Prevents shrinking
  },
  weekendSection: {
    display: 'flex',
    flex: 'none', // Prevents shrinking
  },
  dayColumn: {
    flex: '1 1 0', // Equal width for weekdays
    minWidth: '150px',
    borderRight: '1px solid #ddd',
  },
  weekendColumn: {
    width: '150px', // Fixed width for weekend days
    flex: 'none',
    borderRight: '1px solid #ddd',
  },
};

export default CalendarWidget;